import {
  APIContextOptions,
  createAnnotationAPI,
  createAPIContext,
  createBlobAPI,
  createConfigAPI,
  createPatientAPI,
  createTranscodeAPIv2,
} from "@cur8/api-client";
import { createContext, useCallback, useContext, useMemo } from "react";
import { useAppInsights } from "./AppInsightsContext";
import { useConfig } from "./ConfigContext";
import { useMSAL } from "./MSALContext";

function createClient(options: APIContextOptions) {
  const context = createAPIContext(options);

  return {
    annotation: createAnnotationAPI(context),
    transcode: createTranscodeAPIv2(context),
    blob: createBlobAPI(context),
    config: createConfigAPI(context),
    patient: createPatientAPI(context),
  };
}

export type APIClient = ReturnType<typeof createClient>;

type APIContextState = {
  client?: APIClient;
};

const INITIAL: APIContextState = {
  client: undefined,
};

const Context = createContext<APIContextState>(INITIAL);

interface APIContextProps {
  children: React.ReactNode;
}

export default function APIContext({ children }: APIContextProps) {
  const config = useConfig();
  const { auth, instance } = useMSAL();
  const appInsights = useAppInsights();

  // Telemetry only: each call site reports its own failure to the clinician, naming the
  // operation that failed. A failure no call site catches surfaces through
  // UnexpectedErrorNotice, which listens for the platform's unhandled-rejection event.
  const reportError = useCallback(
    (error: any) => {
      appInsights.trackException(error);
    },
    [appInsights]
  );

  const api = useMemo(() => {
    if (!auth?.account) {
      return {
        client: undefined,
      };
    }

    const request = { account: auth.account, scopes: config.msal.scopes.token };

    const getToken = () => {
      return instance.acquireTokenSilent(request).then((response) => {
        return response.accessToken;
      });
    };

    return {
      client: createClient({
        buildURL: (path) => new URL(`${config.apiBaseUrl}/${path}`),
        getToken,
        onError: reportError,
      }),
    };
  }, [auth, config, instance, reportError]);

  return <Context.Provider value={api}>{children}</Context.Provider>;
}

export function useAPIContext() {
  return useContext(Context);
}

export function useAPIClient() {
  const { client } = useAPIContext();
  if (!client) {
    throw new Error("API Client not initialized");
  }
  return client;
}
